<template>
  <ModalStep
    id="presence-session-modal-content"
    data-test-id="presence-session-modal-content"
    :header="{
      title: t('session.presencesModal.title'),
    }"
  >
    <template #bodyContent>
      <div class="c-info-wrapper">
        <p class="c-info-label" style="text-align: center">
          <strong>{{ t('session.presencesModal.body') }}</strong>
        </p>
      </div>
      <div class="presence-listing">
        <div v-for="mentee in menteesRef" :key="mentee.id" class="presence-card">
          <UserInfo
            :user="mentee"
            :sub-info="
              mentee.roles?.includes(RoleEnumTypeV2.MENTEE)
                ? t('common.mentee.capitalize')
                : t('common.mentor.capitalize')
            "
          />
          <Badge
            :variant="
              getUserSessionStatusBadgeProps(
                menteesPresencesRef.find((presence) => presence.id === mentee.id)?.wasPresent
                  ? SessionStatusEnumType.FINISHED
                  : SessionStatusEnumType.CANCELLED,
              ).variant
            "
          >
            {{
              t(
                getUserSessionStatusBadgeProps(
                  menteesPresencesRef.find((presence) => presence.id === mentee.id)?.wasPresent
                    ? SessionStatusEnumType.FINISHED
                    : SessionStatusEnumType.CANCELLED,
                ).textKey,
              )
            }}
          </Badge>
          <CustomSwitch
            :checked="!!menteesPresencesRef.find((presence) => presence.id === mentee.id)?.wasPresent"
            :aria-label="t('session.presencesModal.presenceFor', { name: `${mentee.firstName} ${mentee.lastName}` })"
            @emit:click="togglePresence(mentee.id)"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="submit"
          color="white"
          :text="t('ui.button.fillLater')"
          @emit:click="onCloseAndFillLater()"
        />
        <CustomButton
          :is-loading="isConfirmPresencesPendingRef"
          usage="button"
          type="submit"
          data-test-id="button-continue-presences"
          color="primary"
          :text="t('ui.button.continue')"
          icon-position="right"
          @emit:click="onFinish"
        >
          <template #icon>
            <ArrowIcon color="white" />
          </template>
        </CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'

import Badge from '@/components/Badge.vue'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import { getMentees, getMentor } from '@/features/groups/utils'
import { V1ConfirmPresencesMutation } from '@/features/sessions/api'
import { useSessionInReview } from '@/features/sessions/hooks/use-in-review-session.hook'
import useSession from '@/features/sessions/hooks/use-session.hook'
import { SessionStatusEnumType } from '@/features/sessions/types'
import { getUserSessionStatusBadgeProps } from '@/features/sessions/utils'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import ArrowIcon from '@/features/ui/components/icons/arrow-icon.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import CustomSwitch from '@/features/ui/components/switch/custom-switch.vue'
import { selectGetReviewSessionModal } from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import UserInfo from '@/features/users/components/user-info/user-info.vue'
import { useI18n } from '@/lib/i18n'
import { invalidateSession, invalidateSessions } from '@/utils/lib/vue-query'
const { t } = useI18n()

const reviewSessionModalRef = selectGetReviewSessionModal(modalsStore)
const selectedSessionInReviewIdRef = computed(() => reviewSessionModalRef.value.attachedData.sessionId ?? null)
const { sessionRef } = useSession(selectedSessionInReviewIdRef)
const menteesRef = computed(() => sessionRef.value?.users.filter(getMentees) ?? [])
const mentorRef = computed(() => sessionRef.value?.users.find(getMentor))

const { onClickNextStep, onCloseAndFillLater } = useSessionInReview()

let menteesPresencesRef = ref<{ id: string; wasPresent: boolean }[]>([])
let mentorPresenceRef = ref<{ id: string; wasPresent: boolean } | null>(null)

// initialize mentees presences with 'true' for all mentees
watch(
  menteesRef,
  (mentees) => {
    menteesPresencesRef.value = mentees.map((mentee) => {
      return {
        id: mentee.id,
        wasPresent: true,
      }
    })
  },
  { immediate: true },
)

// initialize the mentor presence with 'true'
watch(
  mentorRef,
  (mentor) => {
    if (!mentor) return
    mentorPresenceRef.value = {
      id: mentor.id,
      wasPresent: true,
    }
  },
  { immediate: true },
)

// toggle presence of a mentee
function togglePresence(menteeId: string) {
  const menteePresence = menteesPresencesRef.value.find((presence) => presence.id === menteeId)!
  menteePresence.wasPresent = !menteePresence.wasPresent
  menteesPresencesRef.value = [...menteesPresencesRef.value]
}

const { mutate: confirmPresences, isPending: isConfirmPresencesPendingRef } = useMutation({
  mutationFn: V1ConfirmPresencesMutation,
  onSuccess: async () => {
    await invalidateSessions()
    await invalidateSession(reviewSessionModalRef.value.attachedData.sessionId!)
    toast?.success(t('session.presencesModal.success'))

    onClickNextStep()
  },
  onError() {
    toast?.error(t('error.common'))
  },
})

function onFinish() {
  if (!mentorPresenceRef.value) {
    throw new Error('Mentor presence is not defined')
  }

  const presences = [...menteesPresencesRef.value, mentorPresenceRef.value]

  confirmPresences({
    sessionId: selectedSessionInReviewIdRef.value!,
    presences: presences.map((presence) => ({
      userId: presence.id,
      isPresent: presence.wasPresent,
    })),
  })
}
</script>

<style lang="scss">
@import './presences-session-modal-content.scss';
</style>
